import { isoDate } from "../../shared/format";
import type { AppointmentStatus, AppointmentSummary } from "../../shared/types/appointment";

/**
 * The statuses that make a doctor's slot unavailable.
 *
 * <p>This is not a UI preference — it mirrors `AppointmentRepository.countOverlapping`, which is
 * the only thing that decides whether a create attempt returns 409: *"LIVE = REQUESTED, CONFIRMED,
 * CHECKED_IN — terminal statuses (COMPLETED, CANCELLED, NO_SHOW) don't block."* Note what that
 * means and is easy to get wrong: a **COMPLETED** appointment frees its slot again.
 *
 * <p>If this list and the server's ever drift, the screen starts telling reception that a slot is
 * taken when the server would accept it (or the reverse), which is worse than saying nothing —
 * #19880/#19881 were exactly that shape, two copies of one rule.
 */
export const SLOT_BLOCKING_STATUSES: readonly AppointmentStatus[] = [
  "REQUESTED",
  "CONFIRMED",
  "CHECKED_IN",
];

/**
 * The start times (`HH:mm`) this doctor is already booked for on `dateISO`, sorted, de-duplicated.
 *
 * <p>Answers the half of #18681 that the free-slot chips do not: a receptionist filling a standing
 * grid by hand (07:30 and 13:30, two doctors, one day at a time — HIS has no recurring booking)
 * cannot tell whether a 409 means *"someone else has that slot"* or *"you already entered this
 * one"*. Showing the taken starts turns the refusal into the grid they are trying to fill.
 *
 * <p>Times only, deliberately: the appointment rows carry patient names, and the point here is
 * "which cells are full", not who is in them. No PHI reaches the conflict box.
 */
export function takenStartsFor(
  appointments: readonly AppointmentSummary[] | undefined,
  doctorId: string | null | undefined,
  dateISO: string
): string[] {
  if (!appointments || !doctorId || !dateISO) return [];
  const seen = new Set<string>();
  for (const a of appointments) {
    if (a.doctorId !== doctorId) continue;
    if (!SLOT_BLOCKING_STATUSES.includes(a.status)) continue;
    const d = new Date(a.startAt);
    if (Number.isNaN(d.getTime())) continue;
    // The list endpoint is asked for one day, but filter anyway: a caller reusing a week's rows
    // would otherwise show next Tuesday's bookings as today's.
    if (isoDate(d) !== dateISO) continue;
    seen.add(
      `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
    );
  }
  return [...seen].sort();
}

/**
 * #19943 — thời lượng gửi lên khi tạo lịch. Thời lượng dịch vụ chỉ là ƯỚC TÍNH (giờ dự kiến xong);
 * không chọn dịch vụ (hoặc dịch vụ chưa khai thời lượng) thì là 1 khung ở chế độ Theo giờ, 30 phút
 * ở chế độ Theo buổi — khớp `PatientMeService` của Mini App. Không còn 20 phút viết cứng.
 */
export function bookingDurationMinutes(
  serviceDurationMinutes: number | null | undefined,
  slotMinutes: number | null
): number {
  if (serviceDurationMinutes && serviceDurationMinutes > 0) return serviceDurationMinutes;
  return slotMinutes && slotMinutes > 0 ? slotMinutes : 30;
}

/**
 * #19943 — các lịch của bác sĩ chiếm khung bắt đầu lúc `startAt`. Cùng luật với tầng ghi
 * (`DoctorSlotOccupancy` + `AppointmentRepository.countDoctorStartsBetween`): ở chế độ Theo giờ
 * một lịch chiếm đúng MỘT khung tính từ giờ bắt đầu, nên hai lịch va nhau khi giờ bắt đầu cách nhau
 * ít hơn một khung — thời lượng dịch vụ không tham gia. Chế độ Theo buổi (`slotMinutes` null) không
 * xét trùng giờ bác sĩ: giới hạn lượt/buổi quyết.
 */
export function doctorSlotClashes(
  appointments: readonly AppointmentSummary[] | undefined,
  doctorId: string | null | undefined,
  startAt: Date,
  slotMinutes: number | null
): AppointmentSummary[] {
  if (!appointments || !doctorId || !slotMinutes || slotMinutes <= 0) return [];
  const startMs = startAt.getTime();
  if (Number.isNaN(startMs)) return [];
  return appointments.filter((a) => {
    if (a.doctorId !== doctorId) return false;
    if (!SLOT_BLOCKING_STATUSES.includes(a.status)) return false;
    const aStart = new Date(a.startAt).getTime();
    return Math.abs(aStart - startMs) < slotMinutes * 60_000;
  });
}

/** Một nút khung giờ trên màn Tạo lịch hẹn. */
export interface SlotChoice {
  /** "HH:mm" — đúng giá trị gửi lên làm giờ bắt đầu. */
  label: string;
  available: boolean;
  /** Nhãn ngắn khi khung không đặt được; null = trống. */
  reason: string | null;
}

/** Cùng nhãn với `ScheduleService.fullLabel` (#19939): nói lớp nào đầy, không chỉ "Hết chỗ". */
export function fullSlotLabel(reason: string | null | undefined): string {
  if (reason === "booking_session_limit_reached") return "Buổi đủ lượt";
  if (reason === "room_daily_limit_reached") return "Phòng đủ lượt";
  // #19943 — bác sĩ (đã chọn / trực mặc định của phòng) hoặc PHÒNG đã đủ chỗ ở khung này.
  if (reason === "doctor_slot_taken" || reason === "room_slot_taken") return "Đã đặt";
  // #19954 — khung của Buổi khám nằm ngoài giờ trực của bác sĩ đã chọn.
  if (reason === "doctor_not_on_duty") return "BS không trực";
  return "Hết chỗ";
}

/**
 * Lưới khung giờ khi CHƯA chọn bác sĩ, ở chế độ Theo giờ — từ availability chung của ngày
 * (`/v1/booking/availability`, cùng nguồn kênh bệnh nhân dùng): khung đã chia theo "Hình thức giờ",
 * nằm trong Buổi khám / ca trực / giờ làm việc, đã bỏ khung quá giờ. Chế độ Theo buổi trả rỗng.
 */
export function slotChoicesFromDay(
  slots: readonly { startTime: string; full: boolean; reason?: string | null }[] | null | undefined
): SlotChoice[] {
  return (slots ?? []).map((s) => ({
    label: s.startTime.slice(0, 5),
    available: !s.full,
    reason: s.full ? fullSlotLabel(s.reason) : null,
  }));
}
